import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Pencil, Trash2, Copy, ExternalLink, Eye, TrendingUp, Compass
} from 'lucide-react';

/**
 * Extracted from AdminThemeTours.jsx to reduce component size.
 * Renders the theme tours list: mobile cards + desktop table.
 */
export default function ThemeTourTable({
  filteredTours,
  openEditModal,
  setDeleteConfirm,
  toggleTourActive,
  copyTourLink,
  openTourInNewTab,
}) {
  if (filteredTours.length === 0) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="text-center py-12 text-slate-500">
            <Compass className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No theme tours found</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        {/* Mobile Cards View */}
        <div className="lg:hidden divide-y">
          {filteredTours.map((tour) => (
            <div key={tour.id} className={`p-4 space-y-3 ${!tour.is_active ? 'bg-slate-50/50' : ''}`}>
              <div className="flex gap-3">
                <img
                  src={tour.image}
                  alt={tour.title}
                  className={`w-16 h-16 rounded-lg object-cover ${!tour.is_active ? 'opacity-50' : ''}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-xs text-slate-400">#{tour.tour_code}</p>
                  <p className={`font-medium truncate ${!tour.is_active ? 'text-slate-500' : 'text-slate-800'}`}>{tour.title}</p>
                  <p className="text-sm text-slate-500">{tour.destination}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  tour.category === 'international' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                }`}>
                  {tour.category}
                </span>
                <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700 capitalize">
                  {tour.theme}
                </span>
                <span className="text-sm font-semibold text-slate-800">₹{tour.price?.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <div className="flex gap-3">
                  <span className="flex items-center gap-1"><Eye size={12} /> {tour.views || 0}</span>
                  <span className="flex items-center gap-1"><TrendingUp size={12} /> {tour.enquiries || 0}</span>
                </div>
                <button
                  onClick={() => toggleTourActive(tour)}
                  className={`px-2 py-1 text-xs rounded-full font-medium ${
                    tour.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}
                >
                  {tour.is_active ? 'Active' : 'Inactive'}
                </button>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditModal(tour)}>
                  <Pencil size={14} className="mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => copyTourLink(tour)}>
                  <Copy size={14} />
                </Button>
                <Button variant="outline" size="sm" className="text-red-600" onClick={() => setDeleteConfirm(tour)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="text-left text-sm text-slate-600">
                <th className="p-4 font-semibold">Code</th>
                <th className="p-4 font-semibold">Tour</th>
                <th className="p-4 font-semibold">Destination</th>
                <th className="p-4 font-semibold">Price</th>
                <th className="p-4 font-semibold">Category</th>
                <th className="p-4 font-semibold">Theme</th>
                <th className="p-4 font-semibold">Performance</th>
                <th className="p-4 font-semibold">Status</th>
                <th className="p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTours.map((tour) => (
                <tr key={tour.id} className={`border-b hover:bg-slate-50 ${!tour.is_active ? 'bg-slate-50/50' : ''}`}>
                  <td className="p-4">
                    <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                      #{tour.tour_code}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={tour.image}
                        alt={tour.title}
                        className={`w-12 h-12 rounded-lg object-cover ${!tour.is_active ? 'opacity-50' : ''}`}
                      />
                      <div>
                        <p className={`font-medium ${!tour.is_active ? 'text-slate-500' : 'text-slate-800'}`}>{tour.title}</p>
                        <p className="text-sm text-slate-500">{tour.duration}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-slate-600">{tour.destination}</td>
                  <td className="p-4 font-semibold text-slate-800">
                    ₹{tour.price?.toLocaleString()}
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      tour.category === 'international' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {tour.category}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700 capitalize">
                      {tour.theme}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Eye size={14} /> {tour.views || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <TrendingUp size={14} /> {tour.enquiries || 0}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => toggleTourActive(tour)}
                      className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                        tour.is_active 
                          ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                    >
                      {tour.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => copyTourLink(tour)} title="Copy link">
                        <Copy size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openTourInNewTab(tour)} title="Open">
                        <ExternalLink size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEditModal(tour)} className="text-blue-600">
                        <Pencil size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteConfirm(tour)} className="text-red-600">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
